import { RecordCollection } from '../../app/record-collection.js';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, IconButton, Input, Modal, StatusPill, useSnackbar } from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Gauge, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  getApiError,
  inventoryApi,
  inventoryMovementsApi,
  type InventoryAlert,
  type InventoryBalanceRow,
} from '../../lib/api.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  FilterGroup,
  LoadingCard,
  Pagination,
  RequestAlert,
} from '../../app/data-view.js';
import type { InventoryView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import {
  BASE_QUANTITY,
  activityOptions,
  shortUnit,
  trimQuantity,
  type InventoryCapabilities,
} from './shared.js';
import { locationLabel, useInventoryUnits } from './inventory-units.js';

const alertOptions = [
  { value: 'ALL' as const, label: 'Tudo' },
  { value: 'ONLY' as const, label: 'Só o que precisa de compra' },
];
/// O alerta nasce do saldo de agora, não de uma coluna que alguém precisa lembrar de atualizar.
const alertLabels: Record<InventoryAlert, string> = {
  NONE: 'Em dia',
  REORDER: 'Hora de repor',
  BELOW_MINIMUM: 'Abaixo do mínimo',
};
const alertTones: Record<InventoryAlert, 'neutral' | 'warning' | 'danger'> = {
  NONE: 'neutral',
  REORDER: 'warning',
  BELOW_MINIMUM: 'danger',
};

const policySchema = z
  .object({
    minimumQuantity: z.string().trim().regex(BASE_QUANTITY, 'Informe um número válido.'),
    reorderPoint: z.string().trim().regex(BASE_QUANTITY, 'Informe um número válido.'),
  })
  .refine(
    (value) => decimalOf(value.reorderPoint) >= decimalOf(value.minimumQuantity),
    /// Ponto de reposição abaixo do mínimo nunca dispararia antes dele: seria um alerta que só
    /// chega depois de a coisa já ter dado errado.
    { path: ['reorderPoint'], message: 'O ponto de reposição não pode ser menor que o mínimo.' },
  );
type PolicyForm = z.infer<typeof policySchema>;

function decimalOf(value: string): number {
  return Number(value.replace(',', '.'));
}

export function BalancesPanel({
  capabilities,
  view,
  onView,
}: {
  capabilities: InventoryCapabilities;
  view: InventoryView;
  onView: ViewSetter<InventoryView>;
}) {
  const { showUnits } = useInventoryUnits();
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [policyTarget, setPolicyTarget] = useState<InventoryBalanceRow>();
  const locations = useQuery({
    queryKey: ['company', 'inventory', 'locations'],
    queryFn: () => inventoryApi.listLocations('ALL'),
    retry: false,
  });
  const filters = {
    ...(view.search.trim() ? { search: view.search.trim() } : {}),
    ...(view.location ? { locationId: view.location } : {}),
    activity: view.activity,
    alertOnly: view.alert === 'ONLY',
    limit: view.limit,
    offset: view.offset,
  };
  const page = useQuery({
    queryKey: ['company', 'inventory', 'balances', filters],
    queryFn: () => inventoryMovementsApi.balances(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });

  async function removePolicy(row: InventoryBalanceRow) {
    try {
      await inventoryMovementsApi.removeReplenishment(row.product.id, row.location.id);
      await queryClient.invalidateQueries({ queryKey: ['company', 'inventory'] });
      snackbar.success('Reposição removida.');
    } catch (error) {
      snackbar.failure(getApiError(error));
    }
  }

  const total = page.data?.total ?? 0;
  const locationName = locations.data?.find((item) => item.id === view.location)?.displayName;
  const chips = [
    ...(view.alert === 'ALL'
      ? []
      : [
          {
            key: 'alert',
            label: 'Precisa de compra',
            onRemove: () => onView({ alert: 'ALL' }),
          },
        ]),
    ...(view.activity === 'ALL'
      ? []
      : [
          {
            key: 'activity',
            label: activityOptions.find((item) => item.value === view.activity)!.label,
            onRemove: () => onView({ activity: 'ALL' }),
          },
        ]),
    ...(locationName
      ? [{ key: 'location', label: locationName, onRemove: () => onView({ location: '' }) }]
      : []),
  ];

  function replenishment(row: InventoryBalanceRow) {
    return (
      <div>
        <StatusPill tone={alertTones[row.alert]}>{alertLabels[row.alert]}</StatusPill>
        {row.minimumQuantity ? (
          <span className="mt-1 block text-xs text-content-muted">
            mín. {trimQuantity(row.minimumQuantity)} · repor em {trimQuantity(row.reorderPoint!)}
          </span>
        ) : null}
      </div>
    );
  }
  function actions(row: InventoryBalanceRow) {
    return (
      <>
        <IconButton
          label={`Definir reposição de ${row.product.displayName}`}
          icon={<Gauge className="size-4" />}
          onClick={() => setPolicyTarget(row)}
        />
        {row.minimumQuantity ? (
          <IconButton
            label={`Remover reposição de ${row.product.displayName}`}
            tone="danger"
            icon={<Trash2 className="size-4" />}
            onClick={() => void removePolicy(row)}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      <FilterBar
        searchId="inventory-balance-search"
        searchLabel="Buscar no saldo"
        searchPlaceholder="Produto, SKU ou local"
        search={view.search}
        onSearchChange={(search) => onView({ search }, { replace: true })}
        chips={chips}
        onClear={() => onView({ alert: 'ALL', activity: 'ALL', location: '' })}
        count={`${total} ${total === 1 ? 'posição' : 'posições'}`}
        resultsLabel={`Ver ${total} ${total === 1 ? 'posição' : 'posições'}`}
        pageSize={view.limit}
        onPageSizeChange={(limit) => onView({ limit })}
      >
        <FilterGroup
          label="Reposição"
          description="Filtrar posições por necessidade de compra"
          options={alertOptions}
          value={view.alert}
          onChange={(alert) => onView({ alert })}
        />
        <FilterGroup
          label="Produto"
          description="Filtrar posições por situação do produto"
          options={activityOptions}
          value={view.activity}
          onChange={(activity) => onView({ activity })}
        />
        <FilterGroup
          label="Local"
          description="Filtrar posições por local"
          options={[
            { value: '', label: 'Todos' },
            ...(locations.data ?? []).map((item) => ({
              value: item.id,
              label: showUnits
                ? `${item.displayName} · ${item.businessUnitName}`
                : item.displayName,
            })),
          ]}
          value={view.location}
          onChange={(location) => onView({ location })}
        />
      </FilterBar>

      {page.isLoading ? (
        <LoadingCard label="Carregando saldo" />
      ) : page.error ? (
        <ErrorCard message={getApiError(page.error)} />
      ) : total === 0 ? (
        <EmptyCard
          icon={Boxes}
          title="Nada em estoque por aqui"
          description="O saldo nasce dos movimentos: registre uma entrada para o produto aparecer."
        />
      ) : (
        <div className="hue-responsive-collection overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          <RecordCollection
            label="Saldo de estoque por produto, local e lote"
            records={page.data?.rows ?? []}
            recordKey={keyOf}
            columns={[
              {
                key: 'product',
                label: 'Produto',
                value: (row) => row.product.displayName,
                render: (row) => (
                  <div>
                    <strong>{row.product.displayName}</strong>
                    <span className="mt-1 block text-xs text-content-muted">
                      {row.product.sku ?? 'Sem SKU'}
                    </span>
                  </div>
                ),
              },
              {
                key: 'location',
                label: 'Local',
                value: (row) => locationLabel(row.location, showUnits),
                render: (row) => locationLabel(row.location, showUnits),
              },
              {
                key: 'lot',
                label: 'Lote',
                value: (row) => row.lot?.code ?? '',
                render: (row) => row.lot?.code ?? '—',
              },
              {
                key: 'quantity',
                label: 'Saldo',
                value: (row) => Number(row.quantity),
                render: (row) => (
                  <span className="font-semibold tabular-nums">
                    {trimQuantity(row.quantity)} {shortUnit(row.product.baseUnit)}
                  </span>
                ),
              },
              {
                key: 'replenishment',
                label: 'Reposição',
                value: (row) => alertLabels[row.alert],
                render: replenishment,
              },
            ]}
            {...(capabilities.canManageReplenishment ? { actions } : {})}
            card={(row) => (
              <article className="p-4">
                <strong className="block text-sm">{row.product.displayName}</strong>
                <span className="mt-1 block text-xs text-content-muted">
                  {row.product.sku ?? 'Sem SKU'} · {locationLabel(row.location, showUnits)}
                  {row.lot ? ` · lote ${row.lot.code}` : ''}
                </span>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold tabular-nums">
                    {trimQuantity(row.quantity)} {shortUnit(row.product.baseUnit)}
                  </span>
                  {replenishment(row)}
                </div>
                {capabilities.canManageReplenishment ? (
                  <div className="mt-3 flex justify-end gap-1">{actions(row)}</div>
                ) : null}
              </article>
            )}
          />
          <Pagination
            total={total}
            limit={view.limit}
            offset={view.offset}
            onOffsetChange={(offset) => onView({ offset })}
            itemLabel={(count) => (count === 1 ? 'posição' : 'posições')}
          />
        </div>
      )}

      {policyTarget ? (
        <PolicyDialog
          row={policyTarget}
          onClose={() => setPolicyTarget(undefined)}
          onSaved={async () => {
            await queryClient.invalidateQueries({ queryKey: ['company', 'inventory'] });
            setPolicyTarget(undefined);
            snackbar.success('Reposição definida.');
          }}
        />
      ) : null}
    </>
  );
}

function keyOf(row: InventoryBalanceRow): string {
  return `${row.product.id}:${row.location.id}:${row.lot?.id ?? ''}`;
}

function PolicyDialog({
  row,
  onClose,
  onSaved,
}: {
  row: InventoryBalanceRow;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [requestError, setRequestError] = useState<string>();
  const form = useForm<PolicyForm>({
    resolver: zodResolver(policySchema),
    defaultValues: {
      minimumQuantity: row.minimumQuantity ? trimQuantity(row.minimumQuantity) : '',
      reorderPoint: row.reorderPoint ? trimQuantity(row.reorderPoint) : '',
    },
  });
  useEffect(() => {
    form.reset({
      minimumQuantity: row.minimumQuantity ? trimQuantity(row.minimumQuantity) : '',
      reorderPoint: row.reorderPoint ? trimQuantity(row.reorderPoint) : '',
    });
  }, [form, row]);

  async function save(data: PolicyForm) {
    setRequestError(undefined);
    try {
      await inventoryMovementsApi.saveReplenishment({
        productId: row.product.id,
        locationId: row.location.id,
        minimumQuantity: data.minimumQuantity.replace(',', '.'),
        reorderPoint: data.reorderPoint.replace(',', '.'),
      });
      await onSaved();
    } catch (cause) {
      setRequestError(getApiError(cause));
    }
  }

  const unit = shortUnit(row.product.baseUnit);
  return (
    <Modal
      open
      onOpenChange={onClose}
      title="Definir reposição"
      description="O alerta nasce do saldo atual. Demanda de atendimentos futuros entra quando existir protocolo planejado."
    >
      <p className="mb-4 text-sm">
        <strong>{row.product.displayName}</strong> em {row.location.displayName}
      </p>
      <form noValidate onSubmit={(event) => void form.handleSubmit(save)(event)}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={`Estoque mínimo (${unit})`}
            inputMode="decimal"
            error={form.formState.errors.minimumQuantity?.message}
            {...form.register('minimumQuantity')}
          />
          <Input
            label={`Repor a partir de (${unit})`}
            inputMode="decimal"
            error={form.formState.errors.reorderPoint?.message}
            {...form.register('reorderPoint')}
          />
        </div>
        {requestError ? <RequestAlert message={requestError} /> : null}
        <div className="mt-6 flex justify-end gap-3 border-t border-surface-border pt-5">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button loading={form.formState.isSubmitting}>Salvar reposição</Button>
        </div>
      </form>
    </Modal>
  );
}
